import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { io } from 'socket.io-client';
import { useLang } from '../LanguageContext';
import { useFarm } from '../context/FarmContext';
import { useAuth } from '../context/AuthContext';
import { authFetch } from '../utils/authFetch';
import { isRateLimitBackoffActive, signalRateLimitBackoffFromSeconds } from '../utils/rateLimitBackoff';
import { getFarmOverviewPath } from '../utils/farmRoutes';
import { emitJoinFarmRooms, subscribeSocketFarmRooms } from '../utils/farmSocketRooms';
import { getSocketIoServerUrl, SOCKET_IO_CLIENT_OPTIONS } from '../utils/socketIoClientOptions';
import { formatRoomFormApiError } from '../utils/roomFormApiError';
import { isGlobalRole, isFarmLeadRole, normalizeRole } from '../utils/roles';
import { getControllerType, getDeviceOwnership } from '../utils/controllerTypes';
import { getTaskContextDisplay, getTaskDisplayTitle } from '../utils/taskDisplay';
import ShefaPageLoadOverlay from '../components/ui/ShefaPageLoadOverlay';
import FarmRoomCard from '../components/FarmRoomCard';
import RoomFormModal from '../components/RoomFormModal';
import { WifiSetupPanel } from './WifiSetupPage';
import './DashboardPage.css';

const LOCALE_MAP = {
  he: 'he-IL',
  ru: 'ru-RU',
  en: 'en-US',
  es: 'es-ES',
  ua: 'uk-UA',
};

const DASHBOARD_POLL_MS = Number(
  process.env.REACT_APP_FARM_DASHBOARD_POLL_MS || process.env.REACT_APP_ROOM_POLL_MS
) || 60_000;
const OFFLINE_THRESHOLD_MINUTES = Number(process.env.REACT_APP_OFFLINE_THRESHOLD_MINUTES || 5);
const OFFLINE_THRESHOLD_MS = OFFLINE_THRESHOLD_MINUTES * 60 * 1000;
const LIVE_PULSE_WINDOW_MS = 1500;
const CLIMATE_STALE_MS = 10 * 60 * 1000;
const CLIMATE_LIVE_FLASH_MS = 1200;

let sharedDashboardSocket = null;

function supportsWebBluetooth() {
  return Boolean(
    typeof window !== 'undefined' &&
      window.isSecureContext &&
      typeof navigator !== 'undefined' &&
      navigator.bluetooth
  );
}

function hasWifiSetupPermission(user) {
  const permissions = user?.permissions || {};
  if (Array.isArray(permissions)) {
    return permissions.some((permission) =>
      ['admin', 'configureWifi', 'wifiSetup', 'deviceSetup'].includes(String(permission || '').trim())
    );
  }
  return Boolean(
    permissions.configureWifi ||
      permissions.wifiSetup ||
      permissions.deviceSetup ||
      permissions.admin ||
      user?.permissionFlags?.configureWifi
  );
}

function getSharedDashboardSocket() {
  if (!sharedDashboardSocket) {
    sharedDashboardSocket = io(getSocketIoServerUrl(), SOCKET_IO_CLIENT_OPTIONS);
    subscribeSocketFarmRooms(sharedDashboardSocket);
  }
  return sharedDashboardSocket;
}

const TASK_STATUS_LABELS = {
  open: 'open',
  in_progress: 'inProgress',
  testing: 'testing',
  needs: 'needs',
  urgent: 'urgent',
  very_urgent: 'veryUrgent',
  done: 'done',
  rejected: 'rejected',
  closed: 'closed',
};

function normalizeStatus(status) {
  return (status || '').trim().toLowerCase();
}

function checkOnlineStatus(deviceLike = {}) {
  const mqttEnabled =
    deviceLike.mqttEnabled === true ||
    deviceLike.meta?.mqttEnabled === true;
  if (mqttEnabled) {
    return normalizeStatus(deviceLike.status) !== 'offline';
  }
  const source =
    deviceLike.lastSeenAtIso ||
    deviceLike.lastSeen ||
    deviceLike.timestamp ||
    deviceLike.lastUpdated;
  if (!source) return false;
  const lastSeen = new Date(source);
  if (!Number.isFinite(lastSeen.getTime())) return false;
  return Date.now() - lastSeen.getTime() <= OFFLINE_THRESHOLD_MS;
}

function formatDate(iso, locale) {
  if (!iso) return '';
  const date = new Date(iso);
  if (!Number.isFinite(date.getTime())) return '';
  return date.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
}

function formatDateTime(iso, locale) {
  if (!iso) return '';
  const date = new Date(iso);
  if (!Number.isFinite(date.getTime())) return '';
  return date.toLocaleString(locale, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

function formatNumber(val, maxFrac = 1) {
  if (val == null || !Number.isFinite(Number(val))) return null;
  return Number(val).toLocaleString(undefined, {
    maximumFractionDigits: maxFrac,
    minimumFractionDigits: 0,
  });
}

function isRoomClimateStale(iso) {
  if (iso == null || iso === '') return true;
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return true;
  return Date.now() - t > CLIMATE_STALE_MS;
}

function hasMetricValue(value) {
  return typeof value !== 'undefined' && value !== null && Number.isFinite(Number(value));
}

const SENSOR_FIELDS = ['temperature', 'humidity', 'co2', 'vpd', 'ph', 'ec', 'waterLevel', 'orp'];
const SENSOR_FIELD_ALIASES = {
  temperature: ['temperature', 'temp'],
  humidity: ['humidity'],
  co2: ['co2', 'CO2'],
  vpd: ['vpd', 'VPD', 'vpdKpa', 'vpd_kpa'],
  ph: ['ph', 'pH'],
  ec: ['ec', 'EC'],
  waterLevel: ['waterLevel', 'water_level', 'levelCm', 'level_cm', 'level'],
  orp: ['orp', 'ORP'],
};

function hasRealtimeTelemetryPayload(payload = {}) {
  if (!payload || typeof payload !== 'object') return false;
  const latestTelemetry =
    payload.latestTelemetry && typeof payload.latestTelemetry === 'object'
      ? payload.latestTelemetry
      : null;
  if (latestTelemetry?.timestamp) return true;
  if (SENSOR_FIELDS.some((field) => hasMetricValue(pickMetricValue(payload, field)))) return true;
  if (latestTelemetry && SENSOR_FIELDS.some((field) => hasMetricValue(pickMetricValue(latestTelemetry, field)))) {
    return true;
  }
  if (payload.telemetry_reason || payload.telemetryReason) return true;
  if (['rA', 'rD', 'rC', 'rL', 'rb'].some((key) => payload[key] !== undefined && payload[key] !== null)) {
    return true;
  }
  for (const key of ['relays', 'socketStates', 'sockets', 'deviceStatuses']) {
    if (payload[key] && typeof payload[key] === 'object' && Object.keys(payload[key]).length > 0) {
      return true;
    }
  }
  return false;
}
const METRIC_DISPLAY = {
  temperature: { label: 'Temperature', unit: '°C', kind: 'climate', outletKeys: ['ac'] },
  humidity: { label: 'Humidity', unit: '%', kind: 'climate', outletKeys: ['dryer'] },
  co2: { label: 'CO₂', unit: 'ppm', kind: 'climate', outletKeys: ['co2'] },
  vpd: { label: 'VPD', unit: 'kPa', kind: 'climate', outletKeys: ['ac', 'dryer'] },
  ph: { label: 'pH', unit: '', kind: 'water', outletKeys: ['pump', 'valve'] },
  ec: { label: 'EC', unit: '', kind: 'water', outletKeys: ['pump', 'valve'] },
  waterLevel: { label: 'Water level', unit: '', kind: 'water', outletKeys: ['pump', 'valve'] },
  orp: { label: 'ORP', unit: 'mV', kind: 'water', outletKeys: ['pump', 'valve'] },
};
const CONTROLLER_TYPE_LABELS = {
  climate: 'Climate Controller',
  water: 'Water Controller',
  soil: 'Soil Sensor',
  generic: 'Generic Device',
};
const LEGACY_OUTLET_LABELS = {
  ac: 'Cooling / heating',
  dryer: 'Dehumidifier',
  co2: 'CO₂ valve',
  extra: 'Auxiliary equipment',
  pump: 'Pump',
  valve: 'Valve',
};
const FARM_WORKSPACE_COPY = {
  en: {
    workspace: 'Farm workspace',
    subtitle: 'Rooms first. Farm devices separate. Only current alerts.',
    rooms: 'rooms',
    room: 'room',
    activeDevices: 'active devices',
    offline: 'offline',
    lastSensorUpdate: 'Last sensor update',
    dashboardRefreshed: 'Dashboard refreshed',
    noDataYet: 'No data yet',
    latestClimate: 'Latest climate',
    latestWater: 'Latest water',
    activeEquipment: 'Active equipment',
    noEquipmentState: 'No equipment state',
    openRoom: 'Open room',
    alertSingular: 'alert',
    alertPlural: 'alerts',
    wifiSetup: 'Set up Wi-Fi',
    wifiSetupTitle: 'Set up controller Wi-Fi over Bluetooth',
    farmStatus: 'Farm status',
    everythingCalm: 'Everything looks calm',
    itemsToCheck: (count) => `${count} item${count === 1 ? '' : 's'} to check`,
    mainGrowSpaces: 'Main grow spaces',
    devicesOnline: 'Devices online',
    farmDevices: 'Farm devices',
    farmDevicesHint: 'Devices assigned to the farm but not to a room. These can be corridors, shared sensors, water nodes, or equipment outside grow rooms.',
    notAssigned: 'Not assigned to a room',
    waitingReadings: 'Waiting for readings',
    growingSmoothly: 'Growing smoothly',
    needsAttention: 'Needs attention',
    setupNeeded: 'Setup needed',
    deviceOnline: 'Online',
    deviceOffline: 'Offline',
    noRecentSensorValues: 'No recent sensor values',
    noFarmDevices: 'No farm-level devices right now. Devices without a farm are kept out of this grower workspace.',
    attentionTitle: 'Needs attention',
    attentionHint: 'Only current operational items are shown here. Historical tasks stay in Open tasks.',
    attentionEmpty: 'No urgent attention items. Auto/scenario mode is the normal operating state.',
    healthTitle: 'Device health',
    healthHint: 'Grouped by grower-friendly device category.',
    onlineOfTotal: (online, total) => `${online}/${total} online`,
    allReporting: 'All reporting',
    noConnectedDevices: 'No farm devices are connected yet.',
    controllerLabels: CONTROLLER_TYPE_LABELS,
    outletLabels: LEGACY_OUTLET_LABELS,
    metrics: {
      temperature: 'Temperature',
      humidity: 'Humidity',
      co2: 'CO₂',
      vpd: 'VPD',
      ph: 'pH',
      ec: 'EC',
      waterLevel: 'Water level',
      orp: 'ORP',
    },
    autoScenario: 'auto scenario',
    manualLock: 'manual lock',
    manualOverride: 'manual override',
    on: 'on',
    off: 'off',
    unknown: 'unknown',
    day: 'day',
    night: 'night',
    metricAttentionTitle: (metric) => `${metric} needs attention`,
    metricAttentionBody: ({ location, value, range, scenario }) =>
      `${location} is at ${value}, outside the ${range} expected by the ${scenario} scenario.`,
    outletDetail: ({ outlet, state, mode }) => `${outlet} is ${state} and is in ${mode} mode.`,
    connectionTitle: 'Device connection needs attention',
    roomOfflineBody: ({ roomName, count }) =>
      `${roomName} has ${count} offline device${count === 1 ? '' : 's'}. Auto/scenario control may be missing fresh data until connection returns.`,
    staleRoomBody: (roomName) => `${roomName} has no fresh telemetry in the last few minutes.`,
    staleDetail: 'Check telemetry freshness before changing equipment manually.',
    farmDeviceOfflineTitle: 'Farm device is offline',
    farmDeviceOfflineBody: (deviceName) =>
      `${deviceName} is assigned to the farm but not to a room, and it has not sent recent data.`,
    manualTitle: 'Manual control is active',
    manualBody: ({ location, outlet }) => `${outlet} at ${location} is not in auto scenario mode.`,
    manualDetail: 'Manual override or manual lock should be reviewed by the grower.',
    checkConnectionFirst: 'Check connection first; manual override is not the default action.',
  },
  ru: {
    workspace: 'Рабочее пространство фермы',
    subtitle: 'Комнаты — в центре. Устройства фермы — отдельно. Предупреждения — только актуальные.',
    rooms: 'комнат',
    room: 'комната',
    activeDevices: 'активных устройств',
    offline: 'офлайн',
    lastSensorUpdate: 'Последнее обновление сенсоров',
    dashboardRefreshed: 'Дашборд обновлен',
    noDataYet: 'Данных пока нет',
    latestClimate: 'Последний климат',
    latestWater: 'Последняя вода',
    activeEquipment: 'Активное оборудование',
    noEquipmentState: 'Нет состояния оборудования',
    openRoom: 'Открыть комнату',
    alertSingular: 'предупреждение',
    alertPlural: 'предупреждений',
    wifiSetup: 'Настроить Wi‑Fi',
    wifiSetupTitle: 'Настроить Wi‑Fi контроллера через Bluetooth',
    farmStatus: 'Статус фермы',
    everythingCalm: 'Все спокойно',
    itemsToCheck: (count) => `${count} к проверке`,
    mainGrowSpaces: 'Основные зоны выращивания',
    devicesOnline: 'Устройства онлайн',
    farmDevices: 'Устройства фермы',
    farmDevicesHint: 'Устройства назначены ферме, но не комнате: коридоры, общие сенсоры, водные узлы или оборудование вне комнат.',
    notAssigned: 'Не назначены комнате',
    waitingReadings: 'Ждем показания',
    growingSmoothly: 'Растем спокойно',
    needsAttention: 'Требует внимания',
    setupNeeded: 'Нужна настройка',
    deviceOnline: 'Онлайн',
    deviceOffline: 'Офлайн',
    noRecentSensorValues: 'Нет свежих показаний',
    noFarmDevices: 'Устройств фермы вне комнат сейчас нет. Устройства без фермы не показываются в рабочем пространстве.',
    attentionTitle: 'Требует внимания',
    attentionHint: 'Здесь только текущие операционные события. Исторические задачи остаются в открытых задачах.',
    attentionEmpty: 'Срочных событий нет. Авто/сценарий остается нормальным режимом работы.',
    healthTitle: 'Состояние устройств',
    healthHint: 'Группировка по понятным категориям устройств.',
    onlineOfTotal: (online, total) => `${online}/${total} онлайн`,
    allReporting: 'Все передают данные',
    noConnectedDevices: 'Устройства фермы пока не подключены.',
    controllerLabels: {
      climate: 'Климат-контроллер',
      water: 'Водный контроллер',
      soil: 'Датчик почвы',
      generic: 'Обычное устройство',
    },
    outletLabels: {
      ac: 'Охлаждение / обогрев',
      dryer: 'Осушитель',
      co2: 'Клапан CO₂',
      extra: 'Доп. оборудование',
      pump: 'Насос',
      valve: 'Клапан',
    },
    metrics: {
      temperature: 'Температура',
      humidity: 'Влажность',
      co2: 'CO₂',
      vpd: 'VPD',
      ph: 'pH',
      ec: 'EC',
      waterLevel: 'Уровень воды',
      orp: 'ORP',
    },
    autoScenario: 'авто-сценарий',
    manualLock: 'ручная блокировка',
    manualOverride: 'ручное управление',
    on: 'вкл',
    off: 'выкл',
    unknown: 'неизвестно',
    day: 'дневной',
    night: 'ночной',
    metricAttentionTitle: (metric) => `${metric}: требуется внимание`,
    metricAttentionBody: ({ location, value, range, scenario }) =>
      `${location}: ${value}, вне ожидаемого диапазона ${range} для сценария «${scenario}».`,
    outletDetail: ({ outlet, state, mode }) => `${outlet}: ${state}, режим ${mode}.`,
    connectionTitle: 'Проблема связи с устройством',
    roomOfflineBody: ({ roomName, count }) =>
      `${roomName}: ${count} устройств офлайн. Авто/сценарий может работать без свежих данных.`,
    staleRoomBody: (roomName) => `${roomName}: нет свежей телеметрии за последние минуты.`,
    staleDetail: 'Проверьте свежесть телеметрии перед ручным изменением оборудования.',
    farmDeviceOfflineTitle: 'Устройство фермы офлайн',
    farmDeviceOfflineBody: (deviceName) =>
      `${deviceName} назначено ферме, но не комнате, и давно не отправляло данные.`,
    manualTitle: 'Включено ручное управление',
    manualBody: ({ location, outlet }) => `${outlet} в ${location} сейчас не в авто-сценарии.`,
    manualDetail: 'Ручное управление или блокировку должен проверить гроувер.',
    checkConnectionFirst: 'Сначала проверьте связь; ручное управление не является действием по умолчанию.',
  },
};
FARM_WORKSPACE_COPY.es = {
  ...FARM_WORKSPACE_COPY.en,
  workspace: 'Espacio de la granja',
  subtitle: 'Salas primero. Dispositivos de la granja separados. Solo alertas actuales.',
  rooms: 'salas',
  room: 'sala',
  activeDevices: 'dispositivos activos',
  offline: 'sin conexión',
  lastSensorUpdate: 'Última actualización de sensores',
  dashboardRefreshed: 'Panel actualizado',
  noDataYet: 'Sin datos todavía',
  latestClimate: 'Último clima',
  latestWater: 'Última agua',
  openRoom: 'Abrir sala',
  alertSingular: 'alerta',
  alertPlural: 'alertas',
  farmStatus: 'Estado de la granja',
  everythingCalm: 'Todo está tranquilo',
  itemsToCheck: (count) => `${count} por revisar`,
  mainGrowSpaces: 'Zonas principales de cultivo',
  devicesOnline: 'Dispositivos en línea',
  farmDevices: 'Dispositivos de granja',
  farmDevicesHint: 'Dispositivos asignados a la granja pero no a una sala: pasillos, sensores compartidos, nodos de agua o equipos fuera de salas.',
  notAssigned: 'No asignados a una sala',
  waitingReadings: 'Esperando lecturas',
  growingSmoothly: 'Cultivo estable',
  needsAttention: 'Requiere atención',
  setupNeeded: 'Requiere configuración',
  deviceOnline: 'En línea',
  deviceOffline: 'Sin conexión',
  noRecentSensorValues: 'Sin valores recientes',
  activeEquipment: 'Equipo activo',
  noEquipmentState: 'Sin estado de equipo',
  noFarmDevices: 'No hay dispositivos de granja fuera de salas ahora. Los dispositivos sin granja no se muestran aquí.',
  attentionTitle: 'Requiere atención',
  attentionHint: 'Aquí solo aparecen eventos operativos actuales. Las tareas históricas quedan en tareas abiertas.',
  attentionEmpty: 'No hay eventos urgentes. El modo automático/escenario es el estado normal.',
  healthTitle: 'Estado de dispositivos',
  healthHint: 'Agrupado por categorías fáciles para el cultivador.',
  onlineOfTotal: (online, total) => `${online}/${total} en línea`,
  allReporting: 'Todos reportan',
  noConnectedDevices: 'Aún no hay dispositivos conectados.',
  controllerLabels: {
    climate: 'Controlador de clima',
    water: 'Controlador de agua',
    soil: 'Sensor de suelo',
    generic: 'Dispositivo genérico',
  },
  outletLabels: {
    ac: 'Frío / calor',
    dryer: 'Deshumidificador',
    co2: 'Válvula CO₂',
    extra: 'Equipo auxiliar',
    pump: 'Bomba',
    valve: 'Válvula',
  },
  metrics: {
    temperature: 'Temperatura',
    humidity: 'Humedad',
    co2: 'CO₂',
    vpd: 'VPD',
    ph: 'pH',
    ec: 'EC',
    waterLevel: 'Nivel de agua',
    orp: 'ORP',
  },
  autoScenario: 'escenario automático',
  manualLock: 'bloqueo manual',
  manualOverride: 'control manual',
  metricAttentionTitle: (metric) => `${metric} requiere atención`,
  metricAttentionBody: ({ location, value, range, scenario }) =>
    `${location}: ${value}, fuera del rango esperado ${range} para el escenario ${scenario}.`,
  outletDetail: ({ outlet, state, mode }) => `${outlet}: ${state}, modo ${mode}.`,
  connectionTitle: 'Problema de conexión del dispositivo',
  roomOfflineBody: ({ roomName, count }) =>
    `${roomName}: ${count} dispositivo${count === 1 ? '' : 's'} sin conexión. El modo automático/escenario puede no tener datos frescos.`,
  staleRoomBody: (roomName) => `${roomName}: no hay telemetría fresca en los últimos minutos.`,
  staleDetail: 'Comprueba la telemetría antes de cambiar equipos manualmente.',
  farmDeviceOfflineTitle: 'Dispositivo de granja sin conexión',
  farmDeviceOfflineBody: (deviceName) => `${deviceName} está asignado a la granja, no a una sala, y no envió datos recientes.`,
  manualTitle: 'Control manual activo',
  manualBody: ({ location, outlet }) => `${outlet} en ${location} no está en modo automático/escenario.`,
  manualDetail: 'El cultivador debe revisar el override o bloqueo manual.',
  checkConnectionFirst: 'Primero comprueba la conexión; el override manual no es la acción predeterminada.',
  on: 'encendido',
  off: 'apagado',
  unknown: 'desconocido',
  day: 'día',
  night: 'noche',
};
FARM_WORKSPACE_COPY.ua = {
  ...FARM_WORKSPACE_COPY.en,
  workspace: 'Робочий простір ферми',
  subtitle: 'Кімнати — в центрі. Пристрої ферми — окремо. Лише актуальні попередження.',
  rooms: 'кімнат',
  room: 'кімната',
  activeDevices: 'активних пристроїв',
  offline: 'офлайн',
  lastSensorUpdate: 'Останнє оновлення сенсорів',
  dashboardRefreshed: 'Дашборд оновлено',
  noDataYet: 'Даних ще немає',
  latestClimate: 'Останній клімат',
  latestWater: 'Остання вода',
  openRoom: 'Відкрити кімнату',
  alertSingular: 'попередження',
  alertPlural: 'попереджень',
  farmStatus: 'Статус ферми',
  everythingCalm: 'Усе спокійно',
  itemsToCheck: (count) => `${count} до перевірки`,
  mainGrowSpaces: 'Основні зони вирощування',
  devicesOnline: 'Пристрої онлайн',
  farmDevices: 'Пристрої ферми',
  farmDevicesHint: 'Пристрої призначені фермі, але не кімнаті: коридори, спільні сенсори, водні вузли або обладнання поза кімнатами.',
  notAssigned: 'Не призначені кімнаті',
  waitingReadings: 'Очікуємо показники',
  growingSmoothly: 'Ріст стабільний',
  needsAttention: 'Потребує уваги',
  setupNeeded: 'Потрібне налаштування',
  deviceOnline: 'Онлайн',
  deviceOffline: 'Офлайн',
  noRecentSensorValues: 'Немає свіжих показників',
  activeEquipment: 'Активне обладнання',
  noEquipmentState: 'Немає стану обладнання',
  noFarmDevices: 'Наразі немає пристроїв ферми поза кімнатами. Пристрої без ферми тут не показуються.',
  attentionTitle: 'Потребує уваги',
  attentionHint: 'Тут лише поточні операційні події. Історичні задачі залишаються у відкритих задачах.',
  attentionEmpty: 'Термінових подій немає. Авто/сценарій є нормальним режимом.',
  healthTitle: 'Стан пристроїв',
  healthHint: 'Згруповано за зрозумілими категоріями пристроїв.',
  onlineOfTotal: (online, total) => `${online}/${total} онлайн`,
  allReporting: 'Усі передають дані',
  noConnectedDevices: 'Пристрої ферми ще не підключені.',
  controllerLabels: {
    climate: 'Клімат-контролер',
    water: 'Водний контролер',
    soil: 'Датчик ґрунту',
    generic: 'Звичайний пристрій',
  },
  outletLabels: {
    ac: 'Охолодження / обігрів',
    dryer: 'Осушувач',
    co2: 'Клапан CO₂',
    extra: 'Дод. обладнання',
    pump: 'Насос',
    valve: 'Клапан',
  },
  metrics: {
    temperature: 'Температура',
    humidity: 'Вологість',
    co2: 'CO₂',
    vpd: 'VPD',
    ph: 'pH',
    ec: 'EC',
    waterLevel: 'Рівень води',
    orp: 'ORP',
  },
  autoScenario: 'авто-сценарій',
  manualLock: 'ручне блокування',
  manualOverride: 'ручне керування',
  metricAttentionTitle: (metric) => `${metric}: потребує уваги`,
  metricAttentionBody: ({ location, value, range, scenario }) =>
    `${location}: ${value}, поза очікуваним діапазоном ${range} для сценарію «${scenario}».`,
  outletDetail: ({ outlet, state, mode }) => `${outlet}: ${state}, режим ${mode}.`,
  connectionTitle: 'Проблема звʼязку з пристроєм',
  roomOfflineBody: ({ roomName, count }) =>
    `${roomName}: ${count} пристроїв офлайн. Авто/сценарій може працювати без свіжих даних.`,
  staleRoomBody: (roomName) => `${roomName}: немає свіжої телеметрії за останні хвилини.`,
  staleDetail: 'Перевірте свіжість телеметрії перед ручною зміною обладнання.',
  farmDeviceOfflineTitle: 'Пристрій ферми офлайн',
  farmDeviceOfflineBody: (deviceName) => `${deviceName} призначено фермі, але не кімнаті, і він давно не надсилав дані.`,
  manualTitle: 'Активне ручне керування',
  manualBody: ({ location, outlet }) => `${outlet} у ${location} зараз не в авто-сценарії.`,
  manualDetail: 'Ручне керування або блокування має перевірити гровер.',
  checkConnectionFirst: 'Спочатку перевірте звʼязок; ручне керування не є дією за замовчуванням.',
  on: 'увімкнено',
  off: 'вимкнено',
  unknown: 'невідомо',
  day: 'денний',
  night: 'нічний',
};
FARM_WORKSPACE_COPY.he = {
  ...FARM_WORKSPACE_COPY.en,
  workspace: 'מרחב עבודה של החווה',
  subtitle: 'החדרים במרכז. מכשירי החווה בנפרד. רק התראות עדכניות.',
  rooms: 'חדרים',
  room: 'חדר',
  activeDevices: 'מכשירים פעילים',
  offline: 'לא מחובר',
  lastSensorUpdate: 'עדכון חיישנים אחרון',
  dashboardRefreshed: 'רענון לוח הבקרה',
  noDataYet: 'אין נתונים עדיין',
  latestClimate: 'אקלים אחרון',
  latestWater: 'מים אחרונים',
  openRoom: 'פתח חדר',
  alertSingular: 'התראה',
  alertPlural: 'התראות',
  farmStatus: 'מצב החווה',
  everythingCalm: 'הכל רגוע',
  itemsToCheck: (count) => `${count} לבדיקה`,
  mainGrowSpaces: 'אזורי גידול מרכזיים',
  devicesOnline: 'מכשירים מחוברים',
  farmDevices: 'מכשירי חווה',
  farmDevicesHint: 'מכשירים שמשויכים לחווה אך לא לחדר: מסדרונות, חיישנים משותפים, מערכות מים או ציוד מחוץ לחדרים.',
  notAssigned: 'לא משויך לחדר',
  waitingReadings: 'ממתין לקריאות',
  growingSmoothly: 'הגידול יציב',
  needsAttention: 'דורש תשומת לב',
  setupNeeded: 'נדרש חיבור',
  deviceOnline: 'מחובר',
  deviceOffline: 'לא מחובר',
  noRecentSensorValues: 'אין ערכים עדכניים',
  activeEquipment: 'ציוד פעיל',
  noEquipmentState: 'אין מצב ציוד',
  noFarmDevices: 'אין כרגע מכשירי חווה מחוץ לחדרים. מכשירים ללא חווה לא מוצגים כאן.',
  attentionTitle: 'דורש תשומת לב',
  attentionHint: 'מוצגים כאן רק אירועים תפעוליים נוכחיים. משימות היסטוריות נשארות במשימות פתוחות.',
  attentionEmpty: 'אין אירועים דחופים. מצב אוטומטי/תרחיש הוא מצב העבודה הרגיל.',
  healthTitle: 'בריאות מכשירים',
  healthHint: 'מקובץ לפי קטגוריות פשוטות למגדל.',
  onlineOfTotal: (online, total) => `${online}/${total} מחוברים`,
  allReporting: 'כולם מדווחים',
  noConnectedDevices: 'עדיין אין מכשירי חווה מחוברים.',
  controllerLabels: {
    climate: 'בקר אקלים',
    water: 'בקר מים',
    soil: 'חיישן קרקע',
    generic: 'מכשיר כללי',
  },
  outletLabels: {
    ac: 'קירור / חימום',
    dryer: 'מייבש לחות',
    co2: 'שסתום CO₂',
    extra: 'ציוד נוסף',
    pump: 'משאבה',
    valve: 'שסתום',
  },
  metrics: {
    temperature: 'טמפרטורה',
    humidity: 'לחות',
    co2: 'CO₂',
    vpd: 'VPD',
    ph: 'pH',
    ec: 'EC',
    waterLevel: 'מפלס מים',
    orp: 'ORP',
  },
  autoScenario: 'אוטומטי/תרחיש',
  manualLock: 'נעילה ידנית',
  manualOverride: 'שליטה ידנית',
  metricAttentionTitle: (metric) => `${metric} דורש תשומת לב`,
  metricAttentionBody: ({ location, value, range, scenario }) =>
    `${location}: ${value}, מחוץ לטווח הצפוי ${range} בתרחיש ${scenario}.`,
  outletDetail: ({ outlet, state, mode }) => `${outlet}: ${state}, מצב ${mode}.`,
  connectionTitle: 'בעיית חיבור במכשיר',
  roomOfflineBody: ({ roomName, count }) =>
    `${roomName}: ${count} מכשירים לא מחוברים. ייתכן שלאוטומציה אין נתונים עדכניים.`,
  staleRoomBody: (roomName) => `${roomName}: אין טלמטריה טרייה בדקות האחרונות.`,
  staleDetail: 'בדוק עדכניות טלמטריה לפני שינוי ציוד ידני.',
  farmDeviceOfflineTitle: 'מכשיר חווה לא מחובר',
  farmDeviceOfflineBody: (deviceName) => `${deviceName} משויך לחווה אך לא לחדר, ולא שלח נתונים עדכניים.`,
  manualTitle: 'בקרה ידנית פעילה',
  manualBody: ({ location, outlet }) => `${outlet} ב-${location} אינו במצב אוטומטי/תרחיש.`,
  manualDetail: 'המגדל צריך לבדוק override או נעילה ידנית.',
  checkConnectionFirst: 'בדוק חיבור קודם; override ידני אינו פעולת ברירת המחדל.',
  on: 'פועל',
  off: 'כבוי',
  unknown: 'לא ידוע',
  day: 'יום',
  night: 'לילה',
};

function getWorkspaceCopy(lang) {
  return FARM_WORKSPACE_COPY[lang] || FARM_WORKSPACE_COPY.en;
}

function isClimateController(deviceLike = {}) {
  return getControllerType(deviceLike) === 'climate';
}

function hasRelayData(deviceLike = {}) {
  if (typeof deviceLike.hasRelayData === 'boolean') return deviceLike.hasRelayData;
  return [
    deviceLike.relayFan,
    deviceLike.relayDrop,
    deviceLike.relayLight,
    deviceLike.relayReserve,
    deviceLike.relays?.rA,
    deviceLike.relays?.rD,
    deviceLike.relays?.rC,
    deviceLike.relays?.rL,
    deviceLike.relays?.ac,
    deviceLike.relays?.dryer,
    deviceLike.relays?.co2,
    deviceLike.relays?.extra,
  ].some((value) => value !== undefined && value !== null);
}

function mergeSensorDataFromDevice(target, deviceLike = {}) {
  const source = deviceLike.sensorData && typeof deviceLike.sensorData === 'object'
    ? { ...deviceLike, ...deviceLike.sensorData }
    : deviceLike;
  for (const field of SENSOR_FIELDS) {
    const value = pickMetricValue(source, field);
    if (hasMetricValue(value)) {
      if (!target[field]) target[field] = [];
      target[field].push(Number(value));
    }
  }
}

function pickMetricValue(source = {}, field) {
  const aliases = SENSOR_FIELD_ALIASES[field] || [field];
  const mergedSource = source.sensorData && typeof source.sensorData === 'object'
    ? { ...source, ...source.sensorData }
    : source;
  for (const alias of aliases) {
    if (hasMetricValue(mergedSource?.[alias])) return Number(mergedSource[alias]);
  }
  return null;
}

function extractWaterOutletState(source = {}) {
  const candidates = [
    source.outletState,
    source.outState,
    source['outlet-state'],
    source?.config?.out?.status,
    source?.deviceStatuses?.out,
    source?.socketStates?.out,
    source.out,
    source.ro,
  ];
  for (const value of candidates) {
    if (value == null) continue;
    const token = String(value).trim().toLowerCase();
    if (['on', 'open', 'true'].includes(token)) return true;
    if (['off', 'close', 'closed', 'false'].includes(token)) return false;
    const numeric = Number(value);
    if (Number.isFinite(numeric)) return numeric === 1;
  }
  return undefined;
}

function getControllerTypeLabel(deviceLike = {}, copy = FARM_WORKSPACE_COPY.en) {
  return copy.controllerLabels?.[getControllerType(deviceLike)] || copy.controllerLabels?.generic || CONTROLLER_TYPE_LABELS.generic;
}

function formatOutletEquipmentName(outlet = {}, copy = FARM_WORKSPACE_COPY.en) {
  if (outlet?.equipment) return String(outlet.equipment).replace(/_/g, ' ');
  if (outlet?.name) return String(outlet.name).replace(/_/g, ' ');
  return copy.outletLabels?.[outlet?.key] || LEGACY_OUTLET_LABELS[outlet?.key] || 'Equipment';
}

function getDeviceDisplayName(deviceLike = {}) {
  return (
    deviceLike.displayName ||
    deviceLike.customName ||
    deviceLike.name ||
    deviceLike.deviceName ||
    deviceLike.deviceId ||
    deviceLike.id ||
    'Device'
  );
}

function titleCase(value) {
  return String(value || '')
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function getOutletForMetric(deviceLike = {}, metricKey, copy = FARM_WORKSPACE_COPY.en) {
  const outletKeys = METRIC_DISPLAY[metricKey]?.outletKeys || [];
  const outlets = Array.isArray(deviceLike.control?.outlets) ? deviceLike.control.outlets : [];
  const outlet = outlets.find((item) => outletKeys.includes(item.key)) || outlets[0] || null;
  if (outlet) return outlet;
  const legacyKey = outletKeys[0] || 'extra';
  return {
    key: legacyKey,
    equipment: copy.outletLabels?.[legacyKey] || LEGACY_OUTLET_LABELS[legacyKey] || 'Equipment',
    state:
      legacyKey === 'ac'
        ? Boolean(deviceLike.relayFan)
        : legacyKey === 'dryer'
          ? Boolean(deviceLike.relayDrop)
          : legacyKey === 'co2'
            ? Boolean(deviceLike.relayLight)
            : legacyKey === 'extra'
              ? Boolean(deviceLike.relayReserve)
              : deviceLike.outletOn,
    controlState: 'auto',
    automatic: true,
  };
}

function normalizeRange(value) {
  if (Array.isArray(value)) {
    const nums = value.map((item) => Number(item)).filter((item) => Number.isFinite(item));
    return nums.length >= 2 ? [nums[0], nums[1]] : null;
  }
  if (typeof value === 'string') {
    const nums = value
      .split(/\s*-\s*|,|;/)
      .map((item) => Number(item.trim()))
      .filter((item) => Number.isFinite(item));
    return nums.length >= 2 ? [nums[0], nums[1]] : null;
  }
  return null;
}

function getExpectedRange(deviceLike = {}, metricKey) {
  const mode = deviceLike.control?.dayNightMode === 'night' ? 'night' : 'day';
  const thresholds = deviceLike.control?.thresholds || deviceLike.thresholds || {};
  const legacyKeys = {
    temperature: mode === 'night' ? 'tempNight' : 'tempDay',
    humidity: mode === 'night' ? 'humNight' : 'humDay',
    co2: mode === 'night' ? 'co2Night' : 'co2Day',
  };
  return (
    normalizeRange(thresholds?.[mode]?.[metricKey]) ||
    normalizeRange(thresholds?.[legacyKeys[metricKey]]) ||
    null
  );
}

function isMetricOutside(value, range) {
  if (!range || !Number.isFinite(Number(value))) return false;
  const numeric = Number(value);
  return numeric < range[0] || numeric > range[1];
}

function formatMetricValue(metricKey, value, formatNumber) {
  const config = METRIC_DISPLAY[metricKey] || {};
  const decimals = metricKey === 'co2' || metricKey === 'orp' ? 0 : metricKey === 'ph' || metricKey === 'ec' ? 2 : 1;
  const formatted = formatNumber(value, decimals);
  return config.unit ? `${formatted} ${config.unit}` : formatted;
}

function formatRangeText(metricKey, range, formatNumber) {
  if (!range) return 'the expected range';
  const config = METRIC_DISPLAY[metricKey] || {};
  const decimals = metricKey === 'co2' || metricKey === 'orp' ? 0 : metricKey === 'ph' || metricKey === 'ec' ? 2 : 1;
  const text = `${formatNumber(range[0], decimals)}-${formatNumber(range[1], decimals)}`;
  return config.unit ? `${text} ${config.unit}` : text;
}

function getControlModeLabel(outlet = {}, copy = FARM_WORKSPACE_COPY.en) {
  const token = String(outlet.controlState || '').replace(/-/g, '_');
  if (token === 'manual_lock' || outlet.override) return copy.manualLock;
  if (token === 'temporary_manual_override') return copy.manualOverride;
  return copy.autoScenario;
}

function buildDeviceOutletRows(deviceLike = {}, copy = FARM_WORKSPACE_COPY.en) {
  const controlOutlets = Array.isArray(deviceLike.control?.outlets) ? deviceLike.control.outlets : [];
  if (controlOutlets.length) {
    return controlOutlets.map((outlet) => ({
      key: outlet.key,
      name: titleCase(formatOutletEquipmentName(outlet, copy)),
      state: outlet.state,
      mode: getControlModeLabel(outlet, copy),
    }));
  }
  if (!hasRelayData(deviceLike)) return [];
  return [
    { key: 'ac', name: copy.outletLabels.ac, state: Boolean(deviceLike.relayFan), mode: copy.autoScenario },
    { key: 'dryer', name: copy.outletLabels.dryer, state: Boolean(deviceLike.relayDrop), mode: copy.autoScenario },
    { key: 'co2', name: copy.outletLabels.co2, state: Boolean(deviceLike.relayLight), mode: copy.autoScenario },
    { key: 'extra', name: copy.outletLabels.extra, state: Boolean(deviceLike.relayReserve), mode: copy.autoScenario },
  ];
}

function buildDeviceMetricRows(deviceLike = {}, formatNumber, copy = FARM_WORKSPACE_COPY.en) {
  return SENSOR_FIELDS.map((metricKey) => {
    const value = pickMetricValue(deviceLike, metricKey);
    if (!hasMetricValue(value)) return null;
    return {
      key: metricKey,
      label: copy.metrics?.[metricKey] || METRIC_DISPLAY[metricKey]?.label || titleCase(metricKey),
      value: formatMetricValue(metricKey, value, formatNumber),
    };
  }).filter(Boolean);
}

function buildMetricAttentionCards({ rooms, farmDevices, formatNumber, copy }) {
  const cards = [];
  for (const room of rooms) {
    const roomDevices = Array.isArray(room.devices) ? room.devices : [];
    const sensorDataset = room.sensorData && typeof room.sensorData === 'object' ? room.sensorData : {};
    const roomTelemetryStale = isRoomClimateStale(room.lastSensorAt);
    if (!roomTelemetryStale) {
      for (const metricKey of SENSOR_FIELDS) {
        const value = pickMetricValue(sensorDataset, metricKey);
        if (!hasMetricValue(value)) continue;
        const deviceForMetric =
          roomDevices.find((device) => METRIC_DISPLAY[metricKey]?.kind === 'water'
            ? getControllerType(device) === 'water'
            : getControllerType(device) === 'climate') ||
          roomDevices[0] ||
          {};
        const range = getExpectedRange(deviceForMetric, metricKey);
        if (!isMetricOutside(value, range)) continue;
        const outlet = getOutletForMetric(deviceForMetric, metricKey, copy);
        const scenario = deviceForMetric.control?.dayNightMode === 'night' ? copy.night : copy.day;
        cards.push({
          id: `room-${room.roomId}-${metricKey}`,
          severity: 'warning',
          title: copy.metricAttentionTitle(copy.metrics?.[metricKey] || METRIC_DISPLAY[metricKey]?.label || titleCase(metricKey)),
          body: copy.metricAttentionBody({
            location: room.name || copy.room,
            value: formatMetricValue(metricKey, value, formatNumber),
            range: formatRangeText(metricKey, range, formatNumber),
            scenario,
          }),
          detail: copy.outletDetail({
            outlet: titleCase(formatOutletEquipmentName(outlet, copy)),
            state: outlet.state === true ? copy.on : outlet.state === false ? copy.off : copy.unknown,
            mode: getControlModeLabel(outlet, copy),
          }),
          href: `/rooms/${encodeURIComponent(room.roomId)}`,
        });
      }
    }
    if ((room.offlineCount || 0) > 0) {
      cards.push({
        id: `room-${room.roomId}-offline`,
        severity: 'danger',
        title: copy.connectionTitle,
        body: copy.roomOfflineBody({ roomName: room.name || copy.room, count: room.offlineCount }),
        detail: copy.checkConnectionFirst,
        href: `/rooms/${encodeURIComponent(room.roomId)}`,
      });
    }
    if (roomTelemetryStale && (room.deviceCount || 0) > 0) {
      cards.push({
        id: `room-${room.roomId}-stale`,
        severity: 'warning',
        title: copy.connectionTitle,
        body: copy.staleRoomBody(room.name || copy.room),
        detail: copy.staleDetail,
        href: `/rooms/${encodeURIComponent(room.roomId)}`,
      });
    }
    for (const device of roomDevices) {
      const outlets = Array.isArray(device.control?.outlets) ? device.control.outlets : [];
      for (const outlet of outlets) {
        if (!outlet.override && outlet.controlState === 'auto') continue;
        cards.push({
          id: `room-${room.roomId}-${device.deviceId || device.id}-${outlet.key}-manual`,
          severity: 'warning',
          title: copy.manualTitle,
          body: copy.manualBody({
            location: room.name || copy.room,
            outlet: titleCase(formatOutletEquipmentName(outlet, copy)),
          }),
          detail: copy.manualDetail,
          href: `/rooms/${encodeURIComponent(room.roomId)}`,
        });
      }
    }
  }
  for (const device of farmDevices) {
    if (!checkOnlineStatus(device)) {
      cards.push({
        id: `farm-device-${device.deviceId || device.id}-offline`,
        severity: 'danger',
        title: copy.farmDeviceOfflineTitle,
        body: copy.farmDeviceOfflineBody(getDeviceDisplayName(device)),
        detail: copy.checkConnectionFirst,
        href: `/device/${encodeURIComponent(device.deviceId || device.id)}`,
      });
    }
    const outlets = Array.isArray(device.control?.outlets) ? device.control.outlets : [];
    for (const outlet of outlets) {
      if (!outlet.override && outlet.controlState === 'auto') continue;
      cards.push({
        id: `farm-device-${device.deviceId || device.id}-${outlet.key}-manual`,
        severity: 'warning',
        title: copy.manualTitle,
        body: copy.manualBody({
          location: getDeviceDisplayName(device),
          outlet: titleCase(formatOutletEquipmentName(outlet, copy)),
        }),
        detail: copy.manualDetail,
        href: `/device/${encodeURIComponent(device.deviceId || device.id)}`,
      });
    }
  }
  return cards;
}

/** Live room KPIs: dynamic room metrics from all bound devices. */
function recomputeFarmRoomRows(apiRooms, tickerItems, deviceReadingsMap) {
  const mean = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null);
  if (!apiRooms || !apiRooms.length) return [];
  return apiRooms.map((room) => {
    const rid = String(room.roomId);
    const roomDeviceDefs = Array.isArray(room.devices) ? room.devices : [];
    const roomDevicesById = new Map();
    for (const d of roomDeviceDefs) {
      const normalized = { ...d, roomId: room.roomId };
      const id = String(normalized.deviceId || normalized.id || '');
      if (id) roomDevicesById.set(id, normalized);
      else roomDevicesById.set(`room-device-${roomDevicesById.size}`, normalized);
    }
    for (const device of deviceReadingsMap.values()) {
      if (device.roomId && String(device.roomId) === rid) {
        const id = String(device.deviceId || device.id || '');
        if (id) {
          roomDevicesById.set(id, { ...(roomDevicesById.get(id) || {}), ...device, roomId: device.roomId || room.roomId });
        } else {
          roomDevicesById.set(`ad-hoc-${roomDevicesById.size}`, { ...device, roomId: device.roomId || room.roomId });
        }
      }
    }
    const roomDevices = [...roomDevicesById.values()];
    const tItems = (tickerItems || []).filter((x) => x.roomId && String(x.roomId) === rid);
    let onlineCount = room.onlineCount;
    let offlineCount = room.offlineCount;
    if (tItems.length) {
      onlineCount = tItems.filter((t) => checkOnlineStatus(t)).length;
      offlineCount = tItems.length - onlineCount;
    }
    const sensorBuckets = {};
    let hasClimateDevices = false;
    let waterOutletOn = false;
    for (const v of roomDevices) {
      if (isClimateController(v)) hasClimateDevices = true;
      if (checkOnlineStatus(v) && v.outletOn === true) waterOutletOn = true;
      mergeSensorDataFromDevice(sensorBuckets, v);
    }
    const mergedSensorData = {};
    for (const field of SENSOR_FIELDS) {
      const value = mean(sensorBuckets[field] || []);
      if (value != null) mergedSensorData[field] = value;
    }
    let relayLight = false;
    let relayFan = false;
    let relayDrop = false;
    let relayReserve = false;
    let hasClimateRelayData = false;
    for (const v of roomDevices) {
      if (isClimateController(v)) {
        if (hasRelayData(v)) hasClimateRelayData = true;
        if (v.relayLight) relayLight = true;
        if (v.relayFan) relayFan = true;
        if (v.relayDrop) relayDrop = true;
        if (v.relayReserve) relayReserve = true;
      }
    }
    let lastAtMs = 0;
    if (room.lastSensorAt) {
      const t0 = new Date(room.lastSensorAt).getTime();
      if (Number.isFinite(t0)) lastAtMs = t0;
    }
    for (const v of roomDevices) {
      if (v.lastTs) {
        const t1 = v.lastTs instanceof Date ? v.lastTs.getTime() : new Date(v.lastTs).getTime();
        if (Number.isFinite(t1) && t1 > lastAtMs) lastAtMs = t1;
      }
    }
    const lastSensorAt =
      lastAtMs > 0 ? new Date(lastAtMs).toISOString() : room.lastSensorAt != null ? room.lastSensorAt : null;
    return {
      ...room,
      onlineCount,
      offlineCount,
      deviceCount: room.deviceCount,
      sensorData: mergedSensorData,
      meanTemperatureC: mergedSensorData.temperature ?? null,
      meanHumidityPct: mergedSensorData.humidity ?? null,
      meanCo2Ppm: mergedSensorData.co2 ?? null,
      meanVpdKpa: mergedSensorData.vpd ?? null,
      vpd: mergedSensorData.vpd ?? null,
      ph: mergedSensorData.ph ?? null,
      ec: mergedSensorData.ec ?? null,
      waterLevel: mergedSensorData.waterLevel ?? null,
      orp: mergedSensorData.orp ?? null,
      meanPh: mergedSensorData.ph ?? null,
      meanEc: mergedSensorData.ec ?? null,
      meanWaterLevel: mergedSensorData.waterLevel ?? null,
      meanOrp: mergedSensorData.orp ?? null,
      hasClimateDevices,
      hasClimateRelayData,
      hasWaterDevices:
        roomDevices.some((device) => getControllerType(device) === 'water') ||
        ['ph', 'ec', 'waterLevel', 'orp'].some((field) => hasMetricValue(mergedSensorData[field])),
      waterOutletOn,
      relayLight: hasClimateRelayData ? relayLight : false,
      relayFan: hasClimateRelayData ? relayFan : false,
      relayDrop: hasClimateRelayData ? relayDrop : false,
      relayReserve: hasClimateRelayData ? relayReserve || room.relayReserve === true : false,
      lastSensorAt,
      devices: roomDevices,
    };
  });
}

function relayOnFromPayload(val) {
  if (val == null) return undefined;
  const n = Number(val);
  if (!Number.isFinite(n)) return undefined;
  return n > 0.5;
}

export default function DashboardPage() {
  const { farmId: routeFarmId } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const { user, ready: authReady } = useAuth();
  const { effectiveFarmId, contextOverrideId, farmName, farmPlan, setFarmContextId } = useFarm();
  const normalizedRole = normalizeRole(user?.role);
  const isGlobal = isGlobalRole(user?.role);
  const canManageRooms = isGlobal || isFarmLeadRole(user?.role);
  const canConfigureFarmWifi =
    supportsWebBluetooth() &&
    (isGlobal || normalizedRole === 'farm_owner' || hasWifiSetupPermission(user));
  const locale = LOCALE_MAP[lang] || 'en-US';
  const dir = lang === 'he' ? 'rtl' : 'ltr';
  const copy = getWorkspaceCopy(lang);

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [busy, setBusy] = useState(false);
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const hasDataRef = useRef(false);
  const autoRefreshRef = useRef(true);
  const fetchDataRef = useRef(null);
  const contextSynced = useRef(false);
  const apiFarmRoomSummariesRef = useRef([]);
  const deviceReadingsRef = useRef(new Map());
  const deviceIdToRoomIdRef = useRef(new Map());
  const [roomFormOpen, setRoomFormOpen] = useState(false);
  const [roomFormContext, setRoomFormContext] = useState(null);
  const [wifiSetupOpen, setWifiSetupOpen] = useState(false);
  const wifiSetupRef = useRef(null);
  const wifiSetupCloseTimerRef = useRef(null);
  const [flashRoomId, setFlashRoomId] = useState(null);
  const flashTimeoutRef = useRef(null);

  const formatTaskStatus = useCallback(
    (status) => {
      const normalized = normalizeStatus(status);
      const key = TASK_STATUS_LABELS[normalized];
      return key ? t(key) : status;
    },
    [t]
  );

  const saveRoom = useCallback(
    async (body) => {
      if (!roomFormContext?.roomId && !effectiveFarmId) {
        throw new Error(t('roomForm.noFarmContext'));
      }
      if (roomFormContext?.roomId) {
        const res = await authFetch(
          `/api/rooms/${encodeURIComponent(roomFormContext.roomId)}`,
          {
            method: 'PATCH',
            body: JSON.stringify(body),
            headers: { 'Content-Type': 'application/json' },
          },
          { token, farmContextId: effectiveFarmId }
        );
        if (!res.ok) {
          const msg = await formatRoomFormApiError(res, t);
          throw new Error(msg);
        }
        return;
      }
      const res = await authFetch(
        '/api/rooms',
        {
          method: 'POST',
          body: JSON.stringify(body),
          headers: { 'Content-Type': 'application/json' },
        },
        { token, farmContextId: effectiveFarmId }
      );
      if (!res.ok) {
        const msg = await formatRoomFormApiError(res, t);
        throw new Error(msg);
      }
    },
    [roomFormContext, token, effectiveFarmId, t]
  );

  const openCreateRoom = useCallback(() => {
    setRoomFormContext(null);
    setRoomFormOpen(true);
  }, []);

  const openWifiSetup = useCallback(() => {
    if (wifiSetupCloseTimerRef.current) {
      clearTimeout(wifiSetupCloseTimerRef.current);
      wifiSetupCloseTimerRef.current = null;
    }
    setWifiSetupOpen(true);
    wifiSetupRef.current?.startDiscovery();
  }, []);

  const closeWifiSetup = useCallback(() => {
    if (wifiSetupCloseTimerRef.current) {
      clearTimeout(wifiSetupCloseTimerRef.current);
      wifiSetupCloseTimerRef.current = null;
    }
    wifiSetupRef.current?.disconnect();
    setWifiSetupOpen(false);
  }, []);

  const scheduleWifiSetupClose = useCallback(() => {
    if (wifiSetupCloseTimerRef.current) {
      clearTimeout(wifiSetupCloseTimerRef.current);
    }
    wifiSetupCloseTimerRef.current = window.setTimeout(() => {
      wifiSetupCloseTimerRef.current = null;
      closeWifiSetup();
    }, 7000);
  }, [closeWifiSetup]);

  const openEditRoom = useCallback((r) => {
    setRoomFormContext({
      roomId: r.roomId,
      name: r.name,
      typeKey: r.typeKey || '',
      iconKey: r.iconKey || 'leaf',
      description: r.description || '',
    });
    setRoomFormOpen(true);
  }, []);

  useEffect(() => {
    const syncToken = () => {
      setToken(localStorage.getItem('token'));
    };
    syncToken();
    window.addEventListener('storage', syncToken);
    return () => window.removeEventListener('storage', syncToken);
  }, []);

  useEffect(() => {
    if (!authReady || !routeFarmId) return;
    if (isGlobal) {
      setFarmContextId(routeFarmId);
      contextSynced.current = true;
    }
  }, [authReady, routeFarmId, isGlobal, setFarmContextId]);

  useEffect(() => {
    if (!authReady) return;
    if (!isGlobal && user?.farmId && routeFarmId && String(routeFarmId) !== String(user.farmId)) {
      const path = getFarmOverviewPath(user.farmId);
      if (path) navigate(path, { replace: true });
    }
  }, [authReady, user?.farmId, routeFarmId, isGlobal, navigate]);

  const fetchData = useCallback(
    async ({ silent = false } = {}) => {
      if (isRateLimitBackoffActive()) {
        if (!silent) {
          setBusy(false);
          setError(null);
        }
        setFetching(false);
        return;
      }
      if (!effectiveFarmId) {
        if (!silent) {
          setBusy(false);
          setError(null);
        }
        setFetching(false);
        return;
      }
      if (!silent) {
        setBusy(true);
        setError(null);
      }
      setFetching(true);
      try {
        const res = await authFetch(
          '/api/dashboard/overview',
          {},
          {
            token: token || undefined,
            farmContextId: effectiveFarmId,
          }
        );
        if (res.status === 401) {
          throw new Error('Unauthorized');
        }
        if (res.status === 429) {
          const ra = parseInt(res.headers.get('Retry-After') || '120', 10);
          signalRateLimitBackoffFromSeconds(Number.isFinite(ra) && ra > 0 ? ra : 120);
          if (!silent) {
            setBusy(false);
            setError(null);
          }
          setFetching(false);
          return;
        }
        if (res.status === 400) {
          const errBody = await res.json().catch(() => ({}));
          if (errBody?.code === 'FARM_CONTEXT_REQUIRED') {
            throw new Error(t('dashboard.errors.farmContextRequired'));
          }
        }
        if (!res.ok) {
          throw new Error('Failed to load dashboard overview');
        }
        const payload = await res.json();
        const tickerByDeviceId = new Map(
          (Array.isArray(payload.tickerItems) ? payload.tickerItems : [])
            .map((item) => [String(item.deviceId || item.id || ''), item])
            .filter(([id]) => id)
        );
        if (Array.isArray(payload.farmRoomSummaries)) {
          apiFarmRoomSummariesRef.current = JSON.parse(JSON.stringify(payload.farmRoomSummaries));
        } else {
          apiFarmRoomSummariesRef.current = [];
        }
        if (Array.isArray(payload.deviceSnapshots)) {
          const m = new Map();
          const id2r = new Map();
          for (const s of payload.deviceSnapshots) {
            const controllerType = getControllerType(s);
            const tickerItem = tickerByDeviceId.get(String(s.deviceId || s.id || '')) || {};
            m.set(s.deviceId, {
              ...tickerItem,
              ...s,
              displayName: s.displayName || tickerItem.displayName || s.name || s.deviceId,
              status: s.status || tickerItem.status || (tickerItem.offline ? 'offline' : 'online'),
              lastSeenAtIso: s.lastSeenAtIso || tickerItem.lastSeenAtIso || s.lastTs || null,
              controller: controllerType,
              controllerType,
              ownership: s.ownership || getDeviceOwnership(s),
              ph: pickMetricValue(s, 'ph') ?? s.ph,
              ec: pickMetricValue(s, 'ec') ?? s.ec,
              waterLevel: pickMetricValue(s, 'waterLevel') ?? s.waterLevel,
              orp: pickMetricValue(s, 'orp') ?? s.orp,
              outletOn: extractWaterOutletState(s),
              lastTs: s.lastTs != null && s.lastTs !== '' ? new Date(s.lastTs) : null,
            });
            if (s.roomId) id2r.set(s.deviceId, s.roomId);
          }
          deviceReadingsRef.current = m;
          deviceIdToRoomIdRef.current = id2r;
        } else {
          deviceReadingsRef.current = new Map();
          deviceIdToRoomIdRef.current = new Map();
        }
        const farmRoomBase = Array.isArray(payload.farmRoomSummaries) ? payload.farmRoomSummaries : [];
        const tickerItems = Array.isArray(payload.tickerItems) ? payload.tickerItems : [];
        setData({
          summary: payload.summary || {},
          tickerItems,
          deviceSnapshots: [...deviceReadingsRef.current.values()],
          farmRoomSummaries: recomputeFarmRoomRows(
            farmRoomBase,
            tickerItems,
            deviceReadingsRef.current
          ),
          recentAlerts: Array.isArray(payload.recentAlerts) ? payload.recentAlerts : [],
          strains: Array.isArray(payload.strains) ? payload.strains : [],
          news: Array.isArray(payload.news) ? payload.news : [],
          tasks: payload.tasks || { allowed: false, items: [] },
          farm: payload.farm || null,
          generatedAt: payload.generatedAt || new Date().toISOString(),
        });
        hasDataRef.current = true;
        setError(null);
      } catch (err) {
        if (!silent || !hasDataRef.current) {
          setError(err.message || 'Failed to load dashboard overview');
        }
      } finally {
        setFetching(false);
        if (!silent) setBusy(false);
      }
    },
    [token, effectiveFarmId, t]
  );

  useEffect(() => {
    if (!authReady) return;
    if (!routeFarmId) return;
    if (!isGlobal && user?.farmId && String(routeFarmId) !== String(user.farmId)) return;
    if (!effectiveFarmId || String(effectiveFarmId) !== String(routeFarmId)) return;
    fetchData();
  }, [authReady, routeFarmId, user?.farmId, isGlobal, effectiveFarmId, fetchData]);

  useEffect(() => {
    fetchDataRef.current = fetchData;
  }, [fetchData]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!autoRefreshRef.current) return;
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      if (isRateLimitBackoffActive()) return;
      fetchDataRef.current?.({ silent: true });
    }, DASHBOARD_POLL_MS);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!authReady) return;
    if (!token || !effectiveFarmId) return;
    const socket = getSharedDashboardSocket();
    emitJoinFarmRooms(socket);
    const nowIso = () => new Date().toISOString();

    const updateWithStatus = (prev, deviceId, nextStatus, timestamp, options = {}) => {
      if (!prev) return prev;
      const eventDate = timestamp ? new Date(timestamp) : null;
      const eventTimeIso =
        eventDate && Number.isFinite(eventDate.getTime())
          ? eventDate.toISOString()
          : nowIso();
      const isTelemetryEvent = options.isTelemetryEvent === true;
      const pulseUntil = Date.now() + LIVE_PULSE_WINDOW_MS;
      const updateDevice = (item) => {
        const id = String(item?.deviceId || item?.id || '');
        if (!id || id !== deviceId) return item;
        return {
          ...item,
          status: nextStatus || item.status,
          lastSeenAtIso: eventTimeIso,
          ...(isTelemetryEvent
            ? {
                lastUpdated: eventTimeIso,
                timestamp: eventTimeIso,
                lastTelemetryAt: eventTimeIso,
                latestTelemetryAt: eventTimeIso,
                lastRealSensorUpdateAt: eventTimeIso,
                pulseUntil,
              }
            : {}),
        };
      };
      const tickerItems = (prev.tickerItems || []).map(updateDevice);
      const deviceSnapshots = (prev.deviceSnapshots || []).map(updateDevice);
      const onlineCount = tickerItems.filter((item) => checkOnlineStatus(item)).length;
      const offlineCount = Math.max(tickerItems.length - onlineCount, 0);
      const farmRoomSummaries = recomputeFarmRoomRows(
        apiFarmRoomSummariesRef.current,
        tickerItems,
        deviceReadingsRef.current
      );
      return {
        ...prev,
        tickerItems,
        deviceSnapshots,
        farmRoomSummaries,
        generatedAt: isTelemetryEvent ? eventTimeIso : prev.generatedAt,
        summary: {
          ...(prev.summary || {}),
          onlineCount,
          offlineCount,
          ...(isTelemetryEvent ? { lastDataReceivedAt: eventTimeIso } : {}),
        },
      };
    };

    const handleDeviceData = (payload = {}) => {
      const incomingId = String(payload.deviceId || payload.id || '');
      if (!incomingId) return;
      const isOffline = normalizeStatus(payload.status) === 'offline';
      const nextSt = isOffline ? 'offline' : 'online';
      const isTelemetryEvent = hasRealtimeTelemetryPayload(payload);
      if (!isOffline) {
        const roomId = deviceIdToRoomIdRef.current.get(incomingId) || null;
        const prevR = deviceReadingsRef.current.get(incomingId) || {};
        const rC = relayOnFromPayload(
          payload.rC != null
            ? payload.rC
            : payload.relays && payload.relays.co2 != null
              ? payload.relays.co2
              : undefined
        );
        const rL = relayOnFromPayload(
          payload.rL != null
            ? payload.rL
            : payload.relays && (payload.relays.rL != null || payload.relays.extra != null)
              ? payload.relays.rL ?? payload.relays.extra
              : undefined
        );
        const rA = relayOnFromPayload(
          payload.rA != null
            ? payload.rA
            : payload.relays && payload.relays.rA != null
              ? payload.relays.rA
              : payload.relays && payload.relays.ac != null
                ? payload.relays.ac
                : undefined
        );
        const rD = relayOnFromPayload(
          payload.rD != null
            ? payload.rD
            : payload.relays && (payload.relays.rD != null || payload.relays.dryer != null)
              ? payload.relays.rD ?? payload.relays.dryer
              : undefined
        );
        const rawEventTs = payload.timestamp != null && payload.timestamp !== '' ? new Date(payload.timestamp) : new Date();
        const eventTs = Number.isFinite(rawEventTs.getTime()) ? rawEventTs : new Date();
        const outletOn = extractWaterOutletState(payload);
        const nextSensorData = {};
        for (const field of SENSOR_FIELDS) {
          const value = pickMetricValue(payload, field);
          if (hasMetricValue(value)) nextSensorData[field] = value;
        }
        const hasSensorData = Object.keys(nextSensorData).length > 0;
        const hasClimate = ['temperature', 'humidity', 'co2'].some((field) => hasMetricValue(nextSensorData[field]));
        const controllerType = getControllerType(payload);
        deviceReadingsRef.current.set(incomingId, {
          ...prevR,
          roomId: roomId || prevR.roomId,
          deviceId: incomingId,
          lastTs: hasSensorData ? eventTs : prevR.lastTs,
          temperature: nextSensorData.temperature ?? prevR.temperature,
          humidity: nextSensorData.humidity ?? prevR.humidity,
          co2: nextSensorData.co2 ?? prevR.co2,
          vpd: nextSensorData.vpd ?? prevR.vpd,
          ph: nextSensorData.ph ?? prevR.ph,
          ec: nextSensorData.ec ?? prevR.ec,
          waterLevel: nextSensorData.waterLevel ?? prevR.waterLevel,
          orp: nextSensorData.orp ?? prevR.orp,
          sensorData: {
            ...(prevR.sensorData || {}),
            ...nextSensorData,
          },
          controller: controllerType !== 'generic' ? controllerType : prevR.controller ?? 'generic',
          controllerType: controllerType !== 'generic' ? controllerType : prevR.controllerType ?? 'generic',
          relayLight: rC !== undefined ? rC : prevR.relayLight,
          relayFan: rA !== undefined ? rA : prevR.relayFan,
          relayDrop: rD !== undefined ? rD : prevR.relayDrop,
          relayReserve: rL !== undefined ? rL : prevR.relayReserve,
          hasRelayData: rC !== undefined || rL !== undefined || rA !== undefined || rD !== undefined ? true : prevR.hasRelayData,
          outletOn: outletOn !== undefined ? outletOn : prevR.outletOn,
          status: nextSt,
          lastSeenAtIso: payload.timestamp || prevR.lastSeenAtIso,
        });
        if (roomId && hasClimate) {
          if (flashTimeoutRef.current) {
            clearTimeout(flashTimeoutRef.current);
            flashTimeoutRef.current = null;
          }
          setFlashRoomId(roomId);
          flashTimeoutRef.current = window.setTimeout(() => {
            setFlashRoomId((cur) => (cur === roomId ? null : cur));
            flashTimeoutRef.current = null;
          }, CLIMATE_LIVE_FLASH_MS);
        }
      }
      setData((prev) =>
        updateWithStatus(prev, incomingId, nextSt, payload.timestamp, { isTelemetryEvent })
      );
    };

    const handleDeviceStatus = (payload = {}) => {
      const incomingId = String(payload.deviceId || payload.id || '');
      if (!incomingId) return;
      const status = normalizeStatus(payload.status) || 'online';
      const prevR = deviceReadingsRef.current.get(incomingId) || {};
      deviceReadingsRef.current.set(incomingId, {
        ...prevR,
        status,
        lastSeenAtIso: payload.timestamp || prevR.lastSeenAtIso,
      });
      setData((prev) => updateWithStatus(prev, incomingId, status, payload.timestamp));
    };

    socket.on('device_data', handleDeviceData);
    socket.on('device_status', handleDeviceStatus);
    return () => {
      socket.off('device_data', handleDeviceData);
      socket.off('device_status', handleDeviceStatus);
    };
  }, [authReady, token, effectiveFarmId, contextOverrideId]);

  useEffect(
    () => () => {
      if (wifiSetupCloseTimerRef.current) {
        clearTimeout(wifiSetupCloseTimerRef.current);
        wifiSetupCloseTimerRef.current = null;
      }
      if (flashTimeoutRef.current) {
        clearTimeout(flashTimeoutRef.current);
        flashTimeoutRef.current = null;
      }
    },
    []
  );

  const summary = data?.summary || {};
  const dashboardDevices = data?.deviceSnapshots || [];
  const lastSensorUpdateIso = dashboardDevices.reduce((latest, device) => {
    const candidate = device?.lastRealSensorUpdateAt || device?.latestTelemetryAt || null;
    if (!candidate) return latest;
    const candidateDate = new Date(candidate);
    if (!Number.isFinite(candidateDate.getTime())) return latest;
    if (!latest) return candidateDate.toISOString();
    const latestDate = new Date(latest);
    return candidateDate > latestDate ? candidateDate.toISOString() : latest;
  }, null);
  const lastDataTime = lastSensorUpdateIso
    ? formatDateTime(lastSensorUpdateIso, locale)
    : null;
  const dashboardRefreshTime = data?.generatedAt
    ? formatDateTime(data.generatedAt, locale)
    : null;
  useEffect(() => {
    if (typeof console === 'undefined' || typeof console.debug !== 'function') return;
    console.debug('[UI][FreshnessSource][DashboardPage]', {
      visibleLabel: 'Last sensor update',
      source: lastSensorUpdateIso
        ? 'max(deviceSnapshots.lastRealSensorUpdateAt || deviceSnapshots.latestTelemetryAt)'
        : 'none',
      lastSensorUpdateAt: lastSensorUpdateIso,
      lastDataReceivedAt: summary.lastDataReceivedAt || null,
      generatedAt: data?.generatedAt || null,
      dashboardPollMs: DASHBOARD_POLL_MS,
    });
  }, [lastSensorUpdateIso, summary.lastDataReceivedAt, data?.generatedAt]);
  const showInitialLoader = !data && (fetching || busy) && effectiveFarmId;

  const displayFarmName = data?.farm?.name || farmPlan?.farmName || farmName;
  const planTierKey = data?.farm?.planType || farmPlan?.planType;
  const roomSummaries = data?.farmRoomSummaries || [];
  const farmLevelDevices = dashboardDevices.filter(
    (device) => getDeviceOwnership(device) === 'farm_unassigned'
  );
  const attentionCards = buildMetricAttentionCards({
    rooms: roomSummaries,
    farmDevices: farmLevelDevices,
    formatNumber,
    copy,
  });
  const currentAttentionCards = attentionCards.slice(0, 8);
  const roomAlertCounts = new Map();
  const roomMetricAlertLevels = new Map();
  for (const card of attentionCards) {
    const match = String(card.id || '').match(/^room-([^-]+)-/);
    if (match?.[1]) {
      roomAlertCounts.set(match[1], (roomAlertCounts.get(match[1]) || 0) + 1);
      const suffix = String(card.id || '').slice(`room-${match[1]}-`.length);
      if (SENSOR_FIELDS.includes(suffix)) {
        if (!roomMetricAlertLevels.has(match[1])) roomMetricAlertLevels.set(match[1], {});
        roomMetricAlertLevels.get(match[1])[suffix] = card.severity === 'danger' ? 'critical' : 'warning';
      }
    }
  }
  const totalRooms = roomSummaries.length;
  const activeDevices = summary.onlineCount ?? 0;
  const offlineDevices = summary.offlineCount ?? 0;
  const farmStatus =
    !dashboardDevices.length
      ? copy.setupNeeded
      : offlineDevices > 0 || currentAttentionCards.length > 0
        ? copy.needsAttention
        : copy.growingSmoothly;
  const healthByController = Object.entries(copy.controllerLabels).map(([type, label]) => {
    const devicesOfType = dashboardDevices.filter((device) => getControllerType(device) === type);
    const online = devicesOfType.filter((device) => checkOnlineStatus(device)).length;
    return {
      type,
      label,
      total: devicesOfType.length,
      online,
      offline: Math.max(devicesOfType.length - online, 0),
    };
  }).filter((item) => item.total > 0);

  return (
    <div className="dashboard-page" dir={dir}>
      <ShefaPageLoadOverlay show={Boolean(showInitialLoader)} />
      <div className="container dashboard-workspace-container py-4">
        <div className="dashboard-page__header dashboard-workspace-hero">
          <div className="dashboard-workspace-hero__main min-w-0">
            <p className="dashboard-workspace-hero__eyebrow mb-2">{copy.workspace}</p>
            <div className="d-flex align-items-center gap-2 flex-wrap mb-2 min-w-0">
              <h1 className="dashboard-workspace-hero__title text-truncate mb-0" title={displayFarmName || undefined}>
                {displayFarmName || t('nav.farm')}
              </h1>
              <span
                className={`dashboard-status-pill ${
                  farmStatus === copy.growingSmoothly
                    ? 'dashboard-status-pill--good'
                    : farmStatus === copy.needsAttention
                      ? 'dashboard-status-pill--attention'
                      : 'dashboard-status-pill--muted'
                }`}
              >
                {farmStatus}
              </span>
              {planTierKey ? (
                <span className="dashboard-plan-pill flex-shrink-0">
                  {t('dashboard.planBadge', t(`planTier.${planTierKey}`))}
                </span>
              ) : null}
            </div>
            <p className="dashboard-workspace-hero__subtitle mb-0">
              {copy.subtitle}
            </p>
          </div>
          <div className="dashboard-workspace-hero__meta" aria-label={copy.farmStatus}>
            <div>
              <strong>{totalRooms}</strong>
              <span>{totalRooms === 1 ? copy.room : copy.rooms}</span>
            </div>
            <div>
              <strong>{activeDevices}</strong>
              <span>{copy.activeDevices}</span>
            </div>
            <div>
              <strong>{offlineDevices}</strong>
              <span>{copy.offline}</span>
            </div>
            <div>
              <strong>{farmLevelDevices.length}</strong>
              <span>{copy.farmDevices}</span>
            </div>
            <div className="dashboard-workspace-hero__meta-wide">
              <strong>{lastDataTime || copy.noDataYet}</strong>
              <span>{copy.lastSensorUpdate}</span>
            </div>
            <div className="dashboard-workspace-hero__meta-wide">
              <strong>{dashboardRefreshTime || copy.noDataYet}</strong>
              <span>{copy.dashboardRefreshed}</span>
            </div>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        <div className={`dashboard-page__body ${busy ? 'dashboard-page__body--busy' : ''}`}>
          {busy && data && (
            <div className="dashboard-page__overlay" aria-hidden="true">
              <span className="spinner-border" role="status" aria-hidden="true" />
            </div>
          )}
          {showInitialLoader ? null : !effectiveFarmId ? (
            <div className="alert alert-info mb-0" role="status">
              {t('dashboard.errors.farmContextRequired')}
            </div>
          ) : (
            <>
              <section className="dashboard-workspace-section dashboard-rooms-section mb-4" aria-label={t('dashboard.rooms.heading')}>
                <div className="dashboard-section-heading dashboard-section-heading--rooms">
                  <div>
                    <span className="dashboard-section-kicker">{copy.mainGrowSpaces}</span>
                    <h2 className="h4 mb-0">{t('dashboard.rooms.heading')}</h2>
                  </div>
                  {canManageRooms ? (
                    <button
                      type="button"
                      className="btn btn-sm dashboard-secondary-action"
                      onClick={openCreateRoom}
                    >
                      {t('dashboard.rooms.addRoom')}
                    </button>
                  ) : null}
                </div>
                <div className="dashboard-rooms-grid">
                  {(data?.farmRoomSummaries || []).length === 0 ? (
                    <p className="text-muted mb-0">{t('dashboard.rooms.empty')}</p>
                  ) : (
                    (data.farmRoomSummaries || []).map((room) => (
                      <div key={room.roomId} className="shefa-farm-room-col">
                        <FarmRoomCard
                          room={room}
                          t={t}
                          formatNumber={formatNumber}
                          toPath={`/rooms/${encodeURIComponent(room.roomId)}`}
                          onManage={canManageRooms ? () => openEditRoom(room) : undefined}
                          liveFlash={String(flashRoomId) === String(room.roomId)}
                          climateStale={isRoomClimateStale(room.lastSensorAt)}
                          hasClimate={Boolean(room.hasClimateDevices)}
                          hasWater={Boolean(room.hasWaterDevices)}
                          alertCount={roomAlertCounts.get(String(room.roomId)) || 0}
                          alertLevels={roomMetricAlertLevels.get(String(room.roomId))}
                          lastUpdateText={room.lastSensorAt ? formatDateTime(room.lastSensorAt, locale) : copy.noDataYet}
                          labels={{
                            statusAttention: copy.needsAttention,
                            statusGood: copy.growingSmoothly,
                            latestClimate: copy.latestClimate,
                            latestWater: copy.latestWater,
                            activeEquipment: copy.activeEquipment,
                            noEquipmentState: copy.noEquipmentState,
                            noDevices: copy.noConnectedDevices,
                            noDataYet: copy.noDataYet,
                            openRoom: copy.openRoom,
                            tasksLabel: t('nav.tasks'),
                            connectedDevices: t('nav.devices'),
                            statusIdle: copy.setupNeeded,
                            alertSingular: copy.alertSingular,
                            alertPlural: copy.alertPlural,
                            deviceOnline: copy.deviceOnline,
                            deviceOffline: copy.deviceOffline,
                            deviceIdle: copy.setupNeeded,
                            autoScenario: copy.autoScenario,
                            manualLock: copy.manualLock,
                            manualOverride: copy.manualOverride,
                            on: copy.on,
                            off: copy.off,
                            unknown: copy.unknown,
                            outletLabels: copy.outletLabels,
                            controllerLabels: copy.controllerLabels,
                            settings: t('roomCard.configure'),
                            dayNightMismatch: t('roomsPage.dayNightCycleMismatchWarning'),
                            noClimateController: t('roomsPage.noClimateControllerAssigned'),
                            lightStatusUnknown: t('roomsPage.lightStatusUnknown'),
                          }}
                        />
                      </div>
                    ))
                  )}
                </div>
              </section>

              {farmLevelDevices.length ? (
                <section className="dashboard-workspace-section mb-4" aria-label={copy.farmDevices}>
                  <div className="dashboard-section-heading">
                    <div>
                      <h2 className="h5 mb-1">{copy.farmDevices}</h2>
                      <p className="text-muted mb-0">
                        {copy.farmDevicesHint}
                      </p>
                    </div>
                  </div>
                  <div className="dashboard-farm-devices">
                    {farmLevelDevices.map((device) => {
                      const metricRows = buildDeviceMetricRows(device, formatNumber, copy);
                      const outletRows = buildDeviceOutletRows(device, copy);
                      const isOnline = checkOnlineStatus(device);
                      const deviceId = device.deviceId || device.id;
                      return (
                        <Link
                          key={deviceId}
                          to={`/device/${encodeURIComponent(deviceId)}`}
                          className="dashboard-farm-device-card"
                        >
                          <div className="dashboard-farm-device-card__head">
                            <div className="min-w-0">
                              <h3 className="h6 mb-1 text-truncate" title={getDeviceDisplayName(device)}>
                                {getDeviceDisplayName(device)}
                              </h3>
                              <span>{getControllerTypeLabel(device, copy)}</span>
                            </div>
                            <span className={`dashboard-device-state ${isOnline ? 'dashboard-device-state--online' : 'dashboard-device-state--offline'}`}>
                              {isOnline ? copy.deviceOnline : copy.deviceOffline}
                            </span>
                          </div>
                          {metricRows.length ? (
                            <div className="dashboard-device-metrics">
                              {metricRows.slice(0, 4).map((metric) => (
                                <div key={metric.key}>
                                  <strong>{metric.value}</strong>
                                  <span>{metric.label}</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-muted small mb-0">{copy.noRecentSensorValues}</p>
                          )}
                          {outletRows.length ? (
                            <div className="dashboard-device-outlets">
                              {outletRows.slice(0, 4).map((outlet) => (
                                <span key={outlet.key}>
                                  {outlet.name}: {outlet.state === true ? copy.on : outlet.state === false ? copy.off : copy.unknown} · {outlet.mode}
                                </span>
                              ))}
                            </div>
                          ) : null}
                        </Link>
                      );
                    })}
                  </div>
                </section>
              ) : null}

              <section className="dashboard-workspace-section mb-4" aria-label={copy.attentionTitle}>
                <div className="dashboard-section-heading">
                  <div>
                    <h2 className="h5 mb-1">{copy.attentionTitle}</h2>
                    <p className="text-muted mb-0">
                      {copy.attentionHint}
                    </p>
                  </div>
                  <Link className="btn btn-sm dashboard-secondary-action flex-shrink-0" to="/tasks">
                    {t('dashboard.tasks.viewAll')}
                  </Link>
                </div>
                {currentAttentionCards.length ? (
                  <div className="dashboard-attention-grid">
                    {currentAttentionCards.map((card) => (
                      <Link
                        key={card.id}
                        to={card.href || '/devices'}
                        className={`dashboard-attention-card dashboard-attention-card--${card.severity || 'warning'}`}
                      >
                        <div className="dashboard-attention-card__head">
                          <span className="dashboard-attention-card__icon" aria-hidden="true">
                            <i className="fas fa-triangle-exclamation" />
                          </span>
                          <div className="min-w-0">
                            <h3 className="h6 mb-1">{card.title}</h3>
                            <p>{card.body}</p>
                          </div>
                        </div>
                        <small className="dashboard-attention-card__detail">{card.detail}</small>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="dashboard-empty-card dashboard-empty-card--good">
                    {copy.attentionEmpty}
                  </div>
                )}
              </section>

              <section className="dashboard-workspace-section mb-4" aria-label={copy.healthTitle}>
                <div className="dashboard-section-heading">
                  <div>
                    <h2 className="h5 mb-1">{copy.healthTitle}</h2>
                    <p className="text-muted mb-0">{copy.healthHint}</p>
                  </div>
                </div>
                {healthByController.length ? (
                  <div className="dashboard-health-grid">
                    {healthByController.map((item) => (
                      <div key={item.type} className="dashboard-health-card">
                        <span>{item.label}</span>
                        <strong>{copy.onlineOfTotal(item.online, item.total)}</strong>
                        <small>{item.offline ? `${item.offline} ${copy.offline}` : copy.allReporting}</small>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="dashboard-empty-card">{copy.noConnectedDevices}</div>
                )}
              </section>

              <RoomFormModal
                open={roomFormOpen}
                onClose={() => {
                  setRoomFormOpen(false);
                  setRoomFormContext(null);
                }}
                onSubmit={async (body) => {
                  await saveRoom(body);
                  await fetchData({ silent: true });
                }}
                initial={
                  roomFormContext
                    ? {
                        name: roomFormContext.name,
                        typeKey: roomFormContext.typeKey,
                        iconKey: roomFormContext.iconKey,
                        description: roomFormContext.description,
                      }
                    : null
                }
                editing={Boolean(roomFormContext?.roomId)}
                farmLabel={displayFarmName}
                farmId={effectiveFarmId ? String(effectiveFarmId) : ''}
                t={t}
              />

              <div
                className={`modal ${wifiSetupOpen ? 'd-block' : ''}`}
                tabIndex="-1"
                role="dialog"
                aria-modal={wifiSetupOpen ? 'true' : undefined}
                style={{ display: wifiSetupOpen ? 'block' : 'none' }}
                onClick={closeWifiSetup}
              >
                <div
                  className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable"
                  role="document"
                  onClick={(event) => event.stopPropagation()}
                >
                  <div className="modal-content">
                    <div className="modal-header">
                      <h5 className="modal-title">
                        <i className="fas fa-bluetooth-b me-2" aria-hidden="true" />
                        {copy.wifiSetupTitle}
                      </h5>
                      <button type="button" className="btn-close" aria-label="Close" onClick={closeWifiSetup} />
                    </div>
                    <div className="modal-body">
                      <WifiSetupPanel ref={wifiSetupRef} compact onSetupComplete={scheduleWifiSetupClose} />
                    </div>
                  </div>
                </div>
              </div>
              {wifiSetupOpen ? <div className="modal-backdrop fade show" /> : null}

              <section className="dashboard-widgets">
                <div className="row g-4">
                  <div className="col-12 col-lg-6">
                    <div className="card h-100 dashboard-panel-card" style={{ minWidth: 0, flex: '1 1 auto' }}>
                      <div className="card-body d-flex flex-column" style={{ minWidth: 0 }}>
                        <div className="d-flex align-items-center justify-content-between mb-3 gap-2 min-w-0">
                          <h2 className="h5 mb-0 text-truncate">{t('dashboard.strains.title')}</h2>
                          <Link className="btn btn-sm dashboard-secondary-action flex-shrink-0" to="/strains">
                            {t('dashboard.strains.viewAll')}
                          </Link>
                        </div>
                        {data?.strains?.length ? (
                          <div className="dashboard-strains">
                            {data.strains.map((strain) => {
                              const cover = strain.coverImage;
                              const updated = formatDate(strain.updatedAt, locale);
                              const target = `/strain/${encodeURIComponent(strain.slug || strain.id)}`;
                              return (
                                <Link
                                  key={strain.id || strain.slug}
                                  to={target}
                                  className="dashboard-strains__item card"
                                >
                                  {cover ? (
                                    <img
                                      src={cover}
                                      alt={strain.name}
                                      className="card-img-top"
                                      loading="lazy"
                                    />
                                  ) : (
                                    <div className="dashboard-strains__placeholder card-img-top">🌱</div>
                                  )}
                                  <div className="card-body min-w-0">
                                    <h3 className="h6 mb-1 text-truncate" title={strain.name}>
                                      {strain.name}
                                    </h3>
                                    {updated && (
                                      <p className="text-muted small mb-0 text-truncate">
                                        {t('home.strains.updated', updated)}
                                      </p>
                                    )}
                                  </div>
                                </Link>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="text-muted mb-0">{t('dashboard.strains.empty')}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="col-12 col-lg-6">
                    <div className="card h-100 dashboard-panel-card" style={{ minWidth: 0, flex: '1 1 auto' }}>
                      <div className="card-body d-flex flex-column" style={{ minWidth: 0 }}>
                        <div className="d-flex align-items-center justify-content-between mb-3 gap-2 min-w-0">
                          <h2 className="h5 mb-0 text-truncate">{t('dashboard.news.title')}</h2>
                          <Link className="btn btn-sm dashboard-secondary-action flex-shrink-0" to="/news">
                            {t('dashboard.news.viewAll')}
                          </Link>
                        </div>
                        {data?.news?.length ? (
                          <ul className="list-unstyled mb-0">
                            {data.news.map((item) => (
                              <li key={item.id || item.shortId} className="mb-3 min-w-0">
                                <Link to={`/news#${item.id || item.shortId}`} className="dashboard-news__link d-block min-w-0">
                                  <span className="d-block fw-semibold text-truncate" title={item.title}>
                                    {item.title}
                                  </span>
                                  <small className="text-muted">
                                    {formatDate(item.createdAt, locale)}
                                  </small>
                                </Link>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-muted mb-0">{t('dashboard.news.empty')}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row g-4 mt-1">
                  <div className="col-12">
                    <div className="card dashboard-panel-card" style={{ minWidth: 0, flex: '1 1 auto' }}>
                      <div className="card-body" style={{ minWidth: 0 }}>
                        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2 min-w-0">
                          <h2 className="h5 mb-0 text-truncate">{t('dashboard.tasks.title')}</h2>
                          <Link className="btn btn-sm dashboard-secondary-action flex-shrink-0" to="/tasks">
                            {t('dashboard.tasks.viewAll')}
                          </Link>
                        </div>
                        {!data?.tasks?.allowed ? (
                          <p className="text-muted mb-0">{t('dashboard.tasks.superuserOnly')}</p>
                        ) : !data.tasks.items.length ? (
                          <p className="text-muted mb-0">{t('dashboard.tasks.empty')}</p>
                        ) : (
                          <ul className="list-unstyled mb-0 dashboard-tasks">
                            {data.tasks.items.map((task) => {
                              const normalizedTaskStatus = normalizeStatus(task.status);
                              const taskTitle = getTaskDisplayTitle(task);
                              const taskLocation = getTaskContextDisplay(task, undefined, displayFarmName);
                              const taskDate = task.lastActivityAt || task.createdAt;
                              const taskPriority = Number.isFinite(task.priority) ? task.priority : null;
                              const taskIcon =
                                normalizedTaskStatus === 'done' || normalizedTaskStatus === 'closed'
                                  ? 'fas fa-check'
                                  : normalizedTaskStatus === 'in_progress' || normalizedTaskStatus === 'testing'
                                    ? 'fas fa-spinner'
                                    : 'far fa-circle';

                              return (
                                <li key={task.id} className="dashboard-tasks__item">
                                  <span className="dashboard-tasks__status-icon" aria-hidden="true">
                                    <i className={taskIcon} />
                                  </span>
                                  <div className="dashboard-tasks__content min-w-0">
                                    <Link to={`/tasks#${task.id}`} className="dashboard-tasks__title" title={taskTitle}>
                                      {taskTitle}
                                    </Link>
                                    <div className="dashboard-tasks__meta">
                                      {taskLocation ? (
                                        <span className="dashboard-tasks__meta-item">
                                          <i className="fas fa-location-dot" aria-hidden="true" />
                                          <span className="text-truncate">{taskLocation}</span>
                                        </span>
                                      ) : null}
                                      {taskDate ? (
                                        <span className="dashboard-tasks__meta-item">
                                          <i className="far fa-clock" aria-hidden="true" />
                                          <span>{formatDateTime(taskDate, locale)}</span>
                                        </span>
                                      ) : null}
                                    </div>
                                  </div>
                                  <div className="dashboard-tasks__actions">
                                    {taskPriority != null ? (
                                      <span className="dashboard-tasks__chip dashboard-tasks__chip--priority">
                                        {t('priorityLabel', taskPriority)}
                                      </span>
                                    ) : null}
                                    {task.status ? (
                                      <span className={`dashboard-tasks__chip dashboard-tasks__chip--${normalizedTaskStatus || 'open'}`}>
                                        {formatTaskStatus(task.status)}
                                      </span>
                                    ) : null}
                                    <Link to={`/tasks#${task.id}`} className="dashboard-tasks__open">
                                      {t('details')}
                                    </Link>
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
